// configs.ts

declare global {
  interface Window {
    // This should represent the Vapi class constructor if the SDK follows `new Vapi()`
    // Or it's the SDK object if `vapiSDK.run` is indeed how it's used after script load.
    // For now, assuming your `window.vapiSDK.run` pattern is what you have after script load.
    // If it's `new window.Vapi()`, this needs to change.
    vapiSDK?: { run: (config: VapiRunLegacyConfig) => VapiInstance }; // Keep this if loadVapiSDK makes .run available
    Vapi?: new (apiKey: string) => VapiInstance; // For `new Vapi(apiKey)` pattern
  }
}

export interface VapiTool {
  id: string;
  name: string;
  description: string;
}

export interface VapiAssistantOverrides {
  model?: {
    provider?: string;
    model?: string;
    messages?: Array<{
      role: string;
      content: string;
    }>;
    temperature?: number;
    maxTokens?: number;
    tools?: any[];
    toolIds?: string[];
    knowledgeBaseId?: string;
  };
  voice?: {
    provider?: string;
    voiceId?: string;
    speed?: number;
  };
  firstMessage?: string;
  // firstMessageMode - usually a call control param, not an assistant override itself for system prompt
  variableValues?: Record<string, string>;
  metadata?: Record<string, any>; // Assistant-level metadata overrides
  endCallPhrases?: string[];
  silenceTimeoutSeconds?: number;
  maxDurationSeconds?: number;
  recordingEnabled?: boolean; // From SDK docs example
}

export interface VapiConfig {
  apiKey: string; // PUBLIC Key
  sdkUrl: string;
}

// Config for the old .run() pattern if your loadVapiSDK makes it available
// This is what we were using before the SDK docs snippet.
export interface VapiRunLegacyConfig {
  apiKey: string;
  assistant: string; // Assistant ID
  // If overrides were indeed meant for run() in some version
  // assistantOverrides?: VapiAssistantOverrides;
}


// Configuration for defining a transient assistant (passed as the first arg to start if no ID)
export interface VapiTransientAssistantConfig {
  model: { // model is required for transient assistant
    provider: string;
    model: string;
    messages?: Array<{
      role: string;
      content: string;
    }>;
    // ... other model properties
  };
  voice?: { // voice is often required or good to specify
    provider: string;
    voiceId: string;
    // ... other voice properties
  };
  // ... other assistant properties from VapiAssistantOverrides if applicable
  firstMessage?: string;
  variableValues?: Record<string, string>;
  metadata?: Record<string, any>; // Assistant-level metadata
  endCallPhrases?: string[];
  silenceTimeoutSeconds?: number;
  maxDurationSeconds?: number;
  recordingEnabled?: boolean;
}

// Optional third argument to start(assistantId, overrides, callSpecificConfig)
// Or merged into the single object for start(transientAssistantConfig)
export interface VapiCallSpecificConfig {
  // metadata?: Record<string, any>; // Call-specific metadata
  firstMessageMode?: "assistant-speaks-first" | "assistant-waits-for-user" | "assistant-speaks-first-with-model-generated-message";
  // Add other call-specific control parameters here that are not assistant definitions/overrides
}


export interface VapiInstance {
  // Overloaded start method based on official SDK docs
  start(assistantId: string, assistantOverrides?: VapiAssistantOverrides, callConfig?: VapiCallSpecificConfig): Promise<void>;
  start(config: VapiTransientAssistantConfig & VapiCallSpecificConfig): Promise<void>; // For transient assistant

  on: (event: string, callback: (data: any) => void) => void;
  stop: () => void;
  send: (messagePayload: { type: string; message: { role: string; content: string } }) => void;
  setMuted: (muted: boolean) => void;
  isMuted: () => boolean;
  say: (message: string, endCallAfterSpoken?: boolean) => void;
  // Add other methods from SDK docs
}

// This interface might be incorrect if the SDK uses `new Vapi()`.
// If `window.vapiSDK.run` is how your loaded script works, keep it.
// Otherwise, remove VapiSDK and use `new window.Vapi(apiKey)` in useVapiCall.
export interface VapiSDK {
  run: (config: VapiRunLegacyConfig) => VapiInstance;
}

export const VAPI_CONFIG: VapiConfig = {
  apiKey: import.meta.env.VITE_VAPI_API_KEY,
  sdkUrl: 'https://cdn.jsdelivr.net/gh/VapiAI/client-sdk-html-script-tag@latest/dist/assets/index.js'
  // Note: The SDK readme mentions `import Vapi from '@vapi-ai/web';` and `new Vapi('public-key')`.
  // Your `loadVapiSDK` uses a script tag. Check how the global `Vapi` or `vapiSDK` object is exposed by that script.
  // If it exposes `window.Vapi` as a class, you'll use `new window.Vapi(VAPI_CONFIG.apiKey)`
  // If it exposes `window.vapiSDK.run`, then VapiRunLegacyConfig is relevant.
};

export const VAPI_HIDE_CSS = `
  #vapi-support-btn,
  #vapi-sdk-button,
  .vapi-chat-button,
  .ai-sdk-default-button,
  #vapi-widget,
  #vapi-call-widget,
  #vapi-button,
  .vapi-widget,
  .vapi-button,
  .vapi-support-btn,
  [id*="vapi"],
  [class*="vapi"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

export const VAPI_TOOLS: VapiTool[] = [
  {
    id: 'appointment_confirmation',
    name: 'Appointment Confirmation',
    description: 'Preview how your AI Employee calls clients before appointments, to confirm timing, address pre-appointment questions, and reduces no-shows.'
  },
  {
    id: 'recover_cancelled',
    name: 'Recover Cancelled Appointment',
    description: "Preview your AI Employee's instant response to cancellations, handling objections, offering alternative times, and recovering cancelled appointments."
  },
  {
    id: 'recover_no_show',
    name: 'Recover No Show Appointment',
    description: 'Preview the no-show recovery conversation, how your AI Employee reaches out with understanding, uncovers the reason for missing, and smoothly guides them to reschedule.'
  },
  {
    id: 'inbound_call',
    name: 'Inbound Call',
    description: 'Preview your 24/7 AI receptionist in action. Hear how it answers calls, qualifies leads, books appointments, and handles FAQs.'
  },
  {
    id: 'manual_upload',
    name: 'Custom Call',
    description: 'Preview a custom call flow tailored to your exact needs. Test special scenarios like event reminders, membership renewals, or industry-specific conversations - showing how flexible your AI Employee can be.'
  },
];

let sdkLoadPromise: Promise<void> | null = null;

export const loadVapiSDK = (): Promise<void> => {
  if (window.vapiSDK || window.Vapi) { // Check for either pattern
    return Promise.resolve();
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }
  if (!document.getElementById('vapi-hide-style')) {
    const style = document.createElement('style');
    style.id = 'vapi-hide-style';
    style.textContent = VAPI_HIDE_CSS;
    document.head.appendChild(style);
  }
  const script = document.createElement('script');
  script.src = VAPI_CONFIG.sdkUrl;
  script.async = true;
  sdkLoadPromise = new Promise((resolve, reject) => {
    script.onload = () => {
      console.log('VAPI SDK loaded');
      // After loading, check if window.Vapi (class) or window.vapiSDK (object with methods) is available.
      // This example assumes the script tag might expose `window.Vapi` as a class.
      // Or it might expose `window.vapiSDK` directly as an object like in your original VapiSDK interface.
      // Adjust initialization in useVapiCall based on what's actually available.
      if (!window.Vapi && !window.vapiSDK) {
        console.warn("Vapi SDK script loaded, but 'window.Vapi' class or 'window.vapiSDK' object not found.");
      }
      resolve();
    };
    script.onerror = () => {
      console.error('Failed to load VAPI SDK');
      sdkLoadPromise = null;
      reject(new Error('Failed to load VAPI SDK'));
    };
    document.body.appendChild(script);
  });
  return sdkLoadPromise;
};